import { css } from "@styled-system/css";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface HoverCardProps {
  title: string;
  lines: string[];
  children: ReactNode;
}

const GAP = 6;
const MAX_W = 320;

const wrapper = css({ display: "block", minW: "0" });

const card = css({
  position: "fixed",
  zIndex: "[1000]",
  maxW: "[320px]",
  px: "10",
  py: "8",
  bg: "surface.overlay",
  border: "default",
  borderRadius: "4",
  boxShadow: "overlay",
  pointerEvents: "none",
  display: "flex",
  flexDir: "column",
  gap: "3",
});

const cardTitle = css({
  fontSize: "12",
  lineHeight: "default",
  fontWeight: "medium",
  color: "text.bold",
});
const cardLine = css({
  fontFamily: "mono",
  fontSize: "12",
  lineHeight: "default",
  color: "text.subtle",
});

/**
 * Hover (or focus) detail for a calendar chip. Fixed-position via a portal, so
 * calendar cells cannot clip it; above the chip when there is room, else below.
 */
export function HoverCard({ title, lines, children }: HoverCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; above: boolean } | null>(null);

  const show = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const estimatedH = 20 + 18 * (lines.length + 1);
    const above = r.top - GAP - estimatedH >= 0;
    const left = Math.max(8, Math.min(r.left, window.innerWidth - MAX_W - 8));
    setPos({ top: above ? r.top - GAP : r.bottom + GAP, left, above });
  }, [lines.length]);
  const hide = useCallback(() => setPos(null), []);

  useEffect(() => {
    if (!pos) return;
    window.addEventListener("scroll", hide, true);
    return () => window.removeEventListener("scroll", hide, true);
  }, [pos, hide]);

  return (
    <div
      ref={ref}
      className={wrapper}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {pos &&
        createPortal(
          <div
            role="tooltip"
            className={card}
            style={{
              top: pos.top,
              left: pos.left,
              transform: pos.above ? "translateY(-100%)" : undefined,
            }}
          >
            <span className={cardTitle}>{title}</span>
            {lines.map((line) => (
              <span key={line} className={cardLine}>
                {line}
              </span>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}
